/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { createElement, useEffect, useState } from 'react';

// Floating Compute Advisor launcher. The widget itself is the
// <podstack-advisor> web component served from advisor.podstack.ai
// (script tag in public/index.html). Other UI (e.g. the sidebar Support
// button) can open it by dispatching: 
//   window.dispatchEvent(new CustomEvent('podstack-advisor:open'))
const SITE_KEY = '6Ld50FotAAAAABFSMruczVVdhvIYdpXSz8VObAvz';

interface AdvisorFabProps {
  /** Tailwind classes positioning the button (panel stacks above it). */
  buttonPosition?: string;
  panelPosition?: string;
}

const AdvisorFab: React.FC<AdvisorFabProps> = ({
  buttonPosition = 'bottom-6 right-6',
  panelPosition = 'bottom-24 right-6',
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('podstack-advisor:open', onOpen);
    return () => window.removeEventListener('podstack-advisor:open', onOpen);
  }, []);

  return (
    <>
      {open && (
        <div className={`fixed ${panelPosition} z-40 w-[min(420px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl shadow-black/60`}>
          {/* custom element — createElement avoids JSX.IntrinsicElements augmentation */}
          {createElement('podstack-advisor', { 'data-sitekey': SITE_KEY })}
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close Compute Advisor' : 'Open Compute Advisor'}
        aria-expanded={open}
        className={`fixed ${buttonPosition} z-40 w-14 h-14 rounded-full bg-orange-500 text-black shadow-lg shadow-orange-500/40 hover:bg-orange-400 hover:scale-105 transition-all flex items-center justify-center`}
      >
        {open ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 7h10v10H7V7z" /></svg>
        )}
        {!open && (
          <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 border-2 border-black" />
          </span>
        )}
      </button>
    </>
  );
};

export default AdvisorFab;
